import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Phone, ChevronDown, ChevronUp } from "lucide-react";

export default function ShutteringRentalPage() {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "Shuttering Material on Rent - Formwork Solutions | Amar Preet";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Professional shuttering material rental. Acrospan, props, steel plates for concrete formwork. Timely delivery. Delhi NCR, Hyderabad, Vizag.");
    }
  }, []);

  const materials = [
    {
      name: "Acrospan System",
      description: "Advanced formwork system for slab support and concrete structures",
      features: ["Rapid installation", "High load capacity", "Reusable components"]
    },
    {
      name: "Steel Plates & Planks",
      description: "Durable steel plates for scaffolding platforms and formwork",
      features: ["Non-slip surface", "Heavy duty", "Long lasting"]
    },
    {
      name: "Props & Jacks",
      description: "Adjustable vertical support for slab casting",
      features: ["Adjustable height", "High capacity", "Easy operation"]
    },
    {
      name: "Column Boxes",
      description: "Steel formwork for column casting",
      features: ["Multiple sizes", "Clean finish", "Easy stripping"]
    }
  ];

  const applications = [
    "Slab casting and support",
    "Beam and column formwork",
    "Foundation work",
    "Bridge deck casting",
    "Wall formwork",
    "Staircase construction"
  ];

  const faqs = [
    {
      question: "What shuttering materials are available for rent?",
      answer: "We offer Acrospan systems, steel plates & planks, adjustable props & jacks, column boxes, beam bottom forms, and various other formwork materials for concrete construction."
    },
    {
      question: "How do I estimate the quantity needed?",
      answer: "Our technical team provides free site surveys and quantity estimation based on your project drawings. Contact us with your project details for an accurate estimate."
    },
    {
      question: "What is the rental pricing structure?",
      answer: "Pricing is based on quantity, rental duration, and delivery location. We offer competitive monthly rates with discounts for long-term rentals. Contact us for a customized quote."
    },
    {
      question: "Do you provide installation support?",
      answer: "We do not provide installation support. We only supply the shuttering material on rent. Your team or contractor handles the installation and dismantling on site."
    }
  ];

  return (
    <div data-testid="shuttering-rental-page">
      {/* Hero */}
      <section className="bg-secondary py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4 block">Services</span>
              <h1 className="font-heading text-4xl md:text-6xl font-black text-white uppercase tracking-tighter leading-tight mb-6" data-testid="page-headline">
                Shuttering & Formwork Material Rental
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                Professional shuttering and formwork solutions for concrete construction. Quality equipment, fast delivery, and technical support for your project needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-primary text-primary-foreground font-bold uppercase tracking-wider px-8 py-4 hover:bg-primary/90 transition-all border-b-4 border-yellow-600 text-center"
                  data-testid="hero-cta"
                >
                  Get Shuttering Quote
                </Link>
                <a
                  href="tel:+919355744699"
                  className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:text-secondary transition-all text-center flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Talk to Expert
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/assets/shuttering-service.JPG"
                alt="Shuttering and formwork for construction"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Shuttering */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary uppercase tracking-tight mb-6">
              What is Shuttering?
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Shuttering, also known as formwork, is a temporary structure used to support concrete during the casting process. It provides the shape and support for fresh concrete until it gains sufficient strength to support itself.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Our shuttering rental service provides all the essential components for effective concrete formwork, including slab support systems, column forms, beam bottoms, and various steel plates and planks.
            </p>
            <h3 className="font-heading text-xl font-bold text-secondary uppercase tracking-tight mb-4">
              Applications
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {applications.map((app) => (
                <div key={app} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-secondary">{app}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Materials Available */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary uppercase tracking-tight mb-4">
              Shuttering Materials Available
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Complete range of formwork equipment for all concrete construction needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {materials.map((material) => (
              <div key={material.name} className="bg-white p-6 border border-border">
                <h3 className="font-heading text-lg font-bold text-secondary uppercase tracking-tight mb-3">
                  {material.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{material.description}</p>
                <ul className="space-y-2">
                  {material.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary uppercase tracking-tight mb-4">
              Our Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Site Survey", description: "Free site assessment and requirement analysis" },
              { step: "02", title: "Estimation", description: "Accurate quantity estimation based on drawings" },
              { step: "03", title: "Delivery", description: "Timely delivery to your project site" },
              { step: "04", title: "Support", description: "Material delivery to your site" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-primary mx-auto mb-4 flex items-center justify-center">
                  <span className="font-heading text-2xl font-black text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-heading font-bold text-secondary uppercase tracking-tight mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
                Benefits of Renting Shuttering
              </h2>
              <div className="space-y-4">
                {[
                  "Cost effective - no large upfront investment",
                  "Access to quality certified equipment",
                  "Flexibility to scale based on project needs",
                  "No storage or maintenance burden",
                  "Technical support included",
                  "Quick mobilization from our inventory"
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-slate-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-primary-foreground uppercase tracking-tight mb-4">
                Need Shuttering for Your Project?
              </h3>
              <p className="text-primary-foreground/80 mb-6">
                Free site survey and quantity estimation
              </p>
              <Link
                to="/contact"
                className="inline-block bg-secondary text-secondary-foreground font-bold uppercase tracking-wider px-8 py-4 hover:bg-secondary/90 transition-all"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary uppercase tracking-tight mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-border bg-white" data-testid={`faq-${index}`}>
                <button
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  <span className="font-bold text-secondary pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h3 className="font-heading text-xl font-bold text-secondary uppercase tracking-tight mb-6 text-center">
            Related Services
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/scaffolding-rental" className="px-6 py-3 bg-white border border-border hover:border-primary transition-colors">
              Scaffolding Rental
            </Link>
            <Link to="/cuplock-scaffolding" className="px-6 py-3 bg-white border border-border hover:border-primary transition-colors">
              Cuplock Scaffolding
            </Link>
            <Link to="/acrow-span-props" className="px-6 py-3 bg-white border border-border hover:border-primary transition-colors">
              Acrospan Props
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
